import React from 'react';
import { Link } from 'react-router-dom';

import HomeVisits from './services/HomeVisits';
import VideoConsultations from './services/VideoConsultations';
import FamilyPhysicians from './services/FamilyPhysicians';
import LabTests from './services/LabTests';
import SeniorCitizens from './services/SeniorCitizens';
import Vaccinations from './services/Vaccinations';

import Ultrasound from './services/Ultrasound';
import Echo2D from './services/Echo2D';
import DopplerScan from './services/DopplerScan';
import Audiometry from './services/Audiometry';

import InnerHeroBanner from './InnerHeroBanner';
import { Helmet } from 'react-helmet-async';


/* =========================================================
   HEALTH PACKAGE DATA
   ========================================================= */

const healthPackages = [
  {
    id: 'basic',
    name: 'Basic Health Panel',
    price: 'Rs. 2799/-',
    description: 'Essential health screening for routine preventive care.',
    services: [
      'Complete Blood Count',
      'Lipid Profile',
      'Thyroid Profile',
      'Liver Function Test',
      'Kidney Function Test',
      'Vitamin D',
      'Vitamin B12',
      'Total Calcium',
      'HbA1C',
      'Urine Analysis',
      'ECG',
      'Physician Consultation'
    ]
  },

  {
    id: 'executive',
    name: 'Executive Health Panel',
    price: 'Rs. 5999/-',
    description: 'A comprehensive health check for regular monitoring.',
    services: [
      'Complete Blood Count',
      'HbA1C',
      'Thyroid Profile',
      'Total Calcium',
      'Vitamin B12',
      'Vitamin D',
      'Total Iron',
      'Liver Function Test',
      'Lipid Profile',
      'Urine Analysis',
      'FBS',
      'Kidney Function Test',
      'Total Iron',
      'Ultrasound Abdomen',
      'Treadmill Stress Test (TMT)',
      'ECG',
      'Physician Consultation'
    ]
  },

  {
    id: 'master',
    name: 'Master Health Panel',
    price: 'Rs. 6999/-',
    description: 'Advanced screening covering a wider range of health parameters.',
    services: [
      'Fasting Blood Sugar - FBS',
      'Complete Haemogram',
      'HbA1C',
      'Thyroid Profile',
      'Total Calcium',
      'Vitamin B12',
      'Vitamin D',
      'Total Iron',
      'Liver Function Test',
      'Lipid Profile',
      'Urine Analysis',
      'Kidney Function Test',
      'PSA (Male)',
      'Pap Smear (Female)',
      'CA-125',
      'ECG',
      'PFT',
      'Ultrasound Abdomen',
      'Treadmill Stress Test (TMT)',
      '2D Echo',
      'Physiotherapy Consultation',
      'Physician Consultation'
    ]
  },

  {
    id: 'women',
    name: 'Women Wellness Panel',
    price: 'Rs. 4299/-',
    description: 'Focused preventive screening designed for women’s wellness.',
    services: [
      'Complete Blood Count',
      'Total Iron',
      'Thyroid Stimulating Hormone - TSH',
      'S. Prolactin',
      'FSH / LH',
      'S. Progesterone',
      'Estradiol',
      'Pap Smear',
      'Ultrasound Abdomen',
      'Physician Consultation'
    ]
  },

  {
    id: 'premium',
    name: 'Premium Package',
    price: 'Rs. 9999/-',
    description: 'Our most comprehensive package with advanced investigations and consultations.',
    premium: true,
    services: [
      'Fasting Blood Sugar - FBS',
      'Complete Haemogram',
      'HbA1C',
      'Thyroid Profile',
      'Total Calcium',
      'Vitamin B12',
      'Vitamin D',
      'Total Iron',
      'Liver Function Test',
      'Lipid Profile',
      'Urine Analysis',
      'S. Ferritin',
      'Kidney Function Test',
      'PSA (Male)',
      'Pap Smear (Female)',
      'CA-125',
      'ECG',
      'PFT',
      'Ultrasound Abdomen',
      'Treadmill Stress Test (TMT)',
      '2D Echo',
      'Physiotherapy Consultation',
      'Physician Consultation'
    ]
  }
];


/* =========================================================
   HEALTH PACKAGES COMPONENT
   ========================================================= */

function HealthPackages() {
  return (
    <section className="health-packages-section">

      <div className="container">

        {/* Section Heading */}

        <div className="health-packages-heading">

          <div className="health-packages-label">
            HEALTH CHECK PACKAGES
          </div>

          <h2>
            Choose Your <span>Health Package</span>
          </h2>

          <p>
            Comprehensive health screening packages designed to help you
            understand and take better care of your health.
          </p>

        </div>


        {/* Package Cards */}

        <div className="row health-packages-row">

          {healthPackages.map((pkg) => (

            <div
              className="col-xl-4 col-lg-6 col-md-6 col-sm-12"
              key={pkg.id}
            >

              <div
                className={`health-package-card ${
                  pkg.premium ? 'premium-package' : ''
                }`}
              >

                {/* Package Top */}

                <div className="health-package-top">

                  {pkg.premium && (
                    <div className="package-popular">
                      MOST COMPREHENSIVE
                    </div>
                  )}

                  <div className="health-package-icon">
                    <span>+</span>
                  </div>

                  <h3>{pkg.name}</h3>

                  <div className="health-package-price">
                    {pkg.price}
                  </div>

                  <p className="health-package-description">
                    {pkg.description}
                  </p>

                </div>


                {/* Package Services */}

                <div className="health-package-body">

                  <div className="package-includes">
                    Includes
                    <span>{pkg.services.length} services</span>
                  </div>

                  <ul className="health-package-services">

                    {pkg.services.map((service, index) => (

                      <li key={`${pkg.id}-${index}`}>

                        <span className="package-check">
                          ✓
                        </span>

                        <span>
                          {service}
                        </span>

                      </li>

                    ))}

                  </ul>


                  {/* Enquire Button */}

                  <Link
                    to="/contacts"
                    className="health-package-btn"
                  >
                    Enquire Now
                    <span className="package-arrow">
                      →
                    </span>
                  </Link>

                </div>

              </div>

            </div>

          ))}

        </div>

      </div>

    </section>
  );
}


/* =========================================================
   OUR SERVICES PAGE
   ========================================================= */

export default function OurServices() {

  return (
    <>

      <Helmet>

        <title>
          Our Services - Family Doctors at Home | FamPhy
        </title>

        <meta
          name="description"
          content="Explore FamPhy's healthcare services including doctor visits, video consultations, family physicians, laboratory tests, senior citizen care, vaccinations, ultrasound, 2D echo, Doppler scans, audiometry and comprehensive health packages."
        />

        <meta
          name="keywords"
          content="FamPhy services, doctor home visit, video consultation, family physician, lab tests at home, senior citizen care, vaccinations, ultrasound, 2D echo, Doppler scan, audiometry, health packages"
        />

      </Helmet>


      {/* Inner Banner */}

      <InnerHeroBanner />


      {/* Existing Services */}

      <div className="our-services">

        <HomeVisits />

        <VideoConsultations />

        <FamilyPhysicians />

        <LabTests />

        <SeniorCitizens />

        <Vaccinations />

        <Ultrasound />

        <Echo2D />

        <DopplerScan />

        <Audiometry />

      </div>


      {/* =====================================================
          HEALTH PACKAGES
          ===================================================== */}

      <HealthPackages />

    </>
  );
}